<template>
  <div>
    <!-- Sem filtro, o botão: a maioria dos campos oferece tudo, e o editor — o CodeMirror, carregado
         sob demanda — só vem para quem o pede -->
    <v-btn v-if="value === undefined" variant="text" prepend-icon="mdi-filter-outline" @click="$emit('input', '')">
      {{ $tt('option-filter') }}
    </v-btn>
    <!-- O arranjo da linha de regra: a fórmula, a mensagem e o remover -->
    <v-row v-else dense align="start">
      <v-col cols="12" md="7">
        <!-- Obrigatória, com o `*` do `.required`, como a regra: a linha existe para ela -->
        <ontos-formula-input ref="editor" class="required" v-bind="bindings" :label="$tt('option-filter')"
                             :value="value" option-filter :draft="draft" :result-label="resultLabel" :example="example"
                             @input="$emit('input', $event)"/>
      </v-col>
      <v-col cols="10" md="4">
        <!-- Opcional, como a da regra — mas é orientação, e não erro: sem ela, nada embaixo do campo -->
        <v-text-field :label="$tt('rule-message')" :hint="$tt('option-filter-message-hint')" persistent-hint
                      :model-value="message" @update:model-value="$emit('message', $event || undefined)"/>
      </v-col>
      <v-col cols="2" md="1">
        <v-btn icon variant="text" size="small" :title="$tt('remove')" @click="remove">
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, toNative} from 'vue-facing-decorator'
import IdeaProperty from '@/types/IdeaProperty'
import AsyncFormulaInput from '@/components/design/property/asyncFormulaInput'
import {editorsValid} from '@/components/design/property/editors'
import types from '@/model/types'

/**
 * As "Opções permitidas" de um campo de escolha — relacionamento ou seleção: uma fórmula julgada para
 * CADA opção, com `self` sendo ela (`self.ano > 1933`), que pode ler os outros campos do item
 * (`self.estado == estado`). O formulário de preenchimento só oferece as que passam.
 *
 * À parte das regras (decisão do usuário): a regra valida o que se escolheu, e o filtro diz o que se
 * pode escolher. O editor é o MESMO da regra, no modo filtro (`option-filter`), e a mensagem, como
 * a da regra, só que de orientação: embaixo do campo, e na recusa que chega pelo Kanban.
 */
@Component({name: 'ontos-option-filter-input', emits: ['input', 'message'], components: {'ontos-formula-input': AsyncFormulaInput}})
class OptionFilterInput extends Vue {
  /** A fórmula; `undefined` é não ter filtro, e o texto vazio, o filtro que se começou a escrever. */
  @Prop({type: String, default: undefined}) readonly value?: string
  @Prop({type: String, default: undefined}) readonly message?: string
  /** O que o editor de fórmula recebe igual em todo formulário — ver `PropertyInput.formulaBindings`. */
  @Prop({type: Object, required: true}) readonly bindings!: Record<string, unknown>
  /** A propriedade como está no formulário: o destino, o múltiplo e o domínio dizem o que é o `self`. */
  @Prop({type: Object, required: true}) readonly draft!: Partial<IdeaProperty>
  /** O que se escreve aqui, com os nomes da coleção, enquanto o campo está vazio (`formulaExamples`). */
  @Prop({type: String, default: ''}) readonly example!: string

  //Exposto ao template: o filtro dá Sim/Não, o nome que o app dá ao tipo
  resultLabel = types.boolean.name

  /** Sem filtro, sem a mensagem dele. */
  remove() {
    this.$emit('input', undefined)
    this.$emit('message', undefined)
  }

  /** Antes de salvar: o filtro confere — e o vazio que ninguém visitou passa a dizer que é obrigatório. */
  validate(): boolean {
    return editorsValid(this.$refs.editor)
  }
}

export default toNative(OptionFilterInput)
</script>
